import { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Chip,
} from '@mui/material';
import {
  Repeat,
  Today,
  CalendarMonth,
  DateRange,
  Close,
} from '@mui/icons-material';
import type { RecurrenceRule } from '@/types/recurrence';
import { RECURRENCE_PRESETS, getRecurrenceDescription } from '@/types/recurrence';
import { brandColors } from '@/theme/colors';

type RecurrenceSelectorProps = {
  value: RecurrenceRule | null;
  onChange: (recurrence: RecurrenceRule | null) => void;
  disabled?: boolean;
  variant?: 'button' | 'chip';
  size?: 'small' | 'medium';
};

export const RecurrenceSelector = ({
  value,
  onChange,
  disabled = false,
  variant = 'button',
  size = 'small',
}: RecurrenceSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (preset: RecurrenceRule | null) => {
    onChange(preset);
    handleClose();
  };

  const getPresetIcon = (frequency: RecurrenceRule['frequency']) => {
    switch (frequency) {
      case 'daily':
        return <Today fontSize="small" />;
      case 'weekly':
        return <DateRange fontSize="small" />;
      case 'monthly':
        return <CalendarMonth fontSize="small" />;
      case 'yearly':
        return <CalendarMonth fontSize="small" />;
    }
  };

  const displayText = value ? getRecurrenceDescription(value) : 'Repeat';

  if (variant === 'chip') {
    return (
      <>
        <Chip
          icon={<Repeat fontSize="small" />}
          label={displayText}
          onClick={handleClick}
          onDelete={value ? () => onChange(null) : undefined}
          deleteIcon={value ? <Close fontSize="small" /> : undefined}
          size={size}
          disabled={disabled}
          sx={{
            bgcolor: value ? `${brandColors.purple}15` : 'transparent',
            borderColor: value ? brandColors.purple : 'divider',
            color: value ? brandColors.purple : 'text.secondary',
            '&:hover': {
              bgcolor: value ? `${brandColors.purple}25` : 'action.hover',
            },
          }}
          variant="outlined"
        />
        <RecurrenceMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onSelect={handleSelect}
          currentValue={value}
          getPresetIcon={getPresetIcon}
        />
      </>
    );
  }

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={disabled}
        startIcon={<Repeat />}
        size={size}
        sx={{
          color: value ? brandColors.purple : 'text.secondary',
          borderColor: value ? brandColors.purple : 'divider',
          '&:hover': {
            borderColor: brandColors.purple,
            bgcolor: `${brandColors.purple}08`,
          },
        }}
        variant="outlined"
      >
        {displayText}
      </Button>
      <RecurrenceMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onSelect={handleSelect}
        currentValue={value}
        getPresetIcon={getPresetIcon}
      />
    </>
  );
};

type RecurrenceMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelect: (preset: RecurrenceRule | null) => void;
  currentValue: RecurrenceRule | null;
  getPresetIcon: (frequency: RecurrenceRule['frequency']) => React.ReactNode;
};

const RecurrenceMenu = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  currentValue,
  getPresetIcon,
}: RecurrenceMenuProps) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          minWidth: 200,
          maxHeight: 300,
        },
      }}
    >
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          REPEAT
        </Typography>
      </Box>

      {currentValue && (
        <>
          <MenuItem onClick={() => onSelect(null)}>
            <ListItemIcon>
              <Close fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="No repeat" />
          </MenuItem>
          <Divider />
        </>
      )}

      {RECURRENCE_PRESETS.map((preset) => (
        <MenuItem
          key={preset.label}
          onClick={() => onSelect(preset.rule)}
          selected={
            currentValue?.frequency === preset.rule.frequency &&
            currentValue?.interval === preset.rule.interval
          }
        >
          <ListItemIcon>{getPresetIcon(preset.rule.frequency)}</ListItemIcon>
          <ListItemText primary={preset.label} />
        </MenuItem>
      ))}
    </Menu>
  );
};
